import Head from 'next/head'
import Container from '../components/container'
import Navbar from '../components/navbar'
import { useUser } from '../hooks/use-user'
import * as Sentry from '@sentry/node'

import {
  Flex,
  Spinner,
  Image,
  Text,
  Button,
  Box,
  Heading,
  useDisclosure
} from '@chakra-ui/core'
import { useSWRInfinite } from 'swr'
import axios from 'axios'
import { Emoji } from 'emoji-mart'
import Swal from 'sweetalert2'
import useChatra from '../hooks/use-chatra'
import { EventTypeItem } from '../components/event-type/event-type-item'
import { FaPlus } from 'react-icons/fa'
import dynamic from 'next/dynamic'
import { useState } from 'react'

const EventTypeModal = dynamic(() => import('../components/event-type/event-type-modal'))

const pageSize = 6

const EventTypePage = () => {
  useChatra()
  const { user } = useUser({ redirectTo: '/sign-in' })

  const {
    isOpen: isOpenCreate,
    onOpen: onOpenCreate,
    onClose: onCloseCreate
  } = useDisclosure()

  const {
    data,
    error,
    mutate: mutateEventTypes,
    size,
    setSize
  } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.length) return null
      return [
        '/api/eventTypes/my',
        pageIndex * pageSize
      ]
    },
    (_, skip) =>
      axios
        .get(_, {
          params: {
            limit: pageSize,
            skip
          }
        })
        .then(res => res.data),
    { initialSize: 1 }
  )

  const eventTypes = data ? [].concat(...data) : []
  const isLoadingInitialData = !data && !error
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = data?.[0]?.length === 0
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < pageSize)

  const [editableType, setEditableType] = useState(null)

  const onDeleteEvent = async id => {
    const { isConfirmed } = await Swal.fire({
      title: 'Are you sure?',
      text: 'Event type will be deleted.',
      icon: 'warning',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      showCancelButton: true
    })
    if (isConfirmed) {
      try {
        await axios.delete(`/api/eventTypes/${id}`)
        mutateEventTypes()
      } catch (error) {
        Swal.fire('Cannot delete this event type', 'Please try again later!', 'error')
        error.originalMessage = error.message
        error.message = 'Ошибка удаления события'
        Sentry.captureException(error)
      }
    }
  }

  const onEditEvent = async typeId => {
    setEditableType(eventTypes.find(({ id }) => typeId === id))
    onOpenCreate()
  }

  if (!user) {
    return null
  }

  return (
    <div style={{ background: '#F1F2F3', height: '100%', minHeight: '100vh' }}>
      <Head>
        <title>Event types - Hapen</title>
        <link rel='icon' href='/favicon.ico' />
        <link rel='preload' href='/static/logo.svg' as='image ' />
      </Head>
      <Navbar nomb />
      {isOpenCreate && (
        <EventTypeModal
          onClose={onCloseCreate}
          onCreate={() => {
            setEditableType(null)
            mutateEventTypes()
          }}
          editableType={editableType}
          createEndpoint='/api/eventTypes'
        />
      )}
      {
          eventTypes.length > 0 && (
            <Container
              maxW='924px'
              paddingTop={['20px', '40px', '60px', '60px']}
              paddingBottom={['20px', '30px', '50px', '50px']}
            >

              <Flex
                justifyContent='space-between' alignItems={['flex-start', 'center', 'center', 'center']}
                flexDirection={['column', 'row', 'row', 'row']}
              >
                <Box>
                  <Heading
                    as='h1' color='#101010'
                    fontSize='2rem'
                    fontWeight='700' lineHeight='2rem'
                    marginBottom='12px'
                  >
                    Event Types
                  </Heading>
                  <Text color='#6A6E7D' fontSize='1rem' lineHeight='1.25rem'>
                    Create a booking page for a specific meeting
                  </Text>
                </Box>
                <Button
                  background='#4065FF'
                  fontWeight='700'
                  fontSize={['0.875rem', '1rem', '1rem', '1rem']}
                  lineHeight='1.5rem' color='#fff' borderRadius='6px'
                  height='40px'
                  marginTop={['20px', '0', '0', '0']}
                  onClick={(e) => onOpenCreate()}
                  _hover={{
                    background: '#0031ff'
                  }}
                >
                  <Box
                    as={FaPlus} size='17px' color='#fff'
                    marginRight='7px'
                  />New Event Type
                </Button>
              </Flex>
            </Container>
          )
        }

      <Container
        maxW='924px'
        paddingBottom={['20px', '30px', '50px', '50px']}
      >
        {
          eventTypes.map(event => (
            <EventTypeItem
              key={event.id}
              onDelete={onDeleteEvent}
              createEndpoint='/api/eventTypes'
              onSwitched={() => {
                mutateEventTypes()
              }}
              onEdit={onEditEvent}
              {...event}
              emoji={<Emoji size={32} set='apple' emoji={event.emoji} />}
            />
          ))
        }
        {!eventTypes && (
          <Flex justifyContent='center' alignItems='center' minHeight='40vh'>
            <Spinner color='primary' size='xl' />
          </Flex>
        )}
        {!isReachingEnd && (
          <Flex justifyContent='center' alignItems='center' minHeight='15vh'>
            <Button
              background='#4065FF'
              fontWeight='700'
              fontSize={['0.875rem', '1rem', '1rem', '1rem']}
              lineHeight='1.5rem' color='#fff' borderRadius='6px'
              height='40px'
              _hover={{
                background: '#0031ff'
              }}
              isDisabled={isLoadingMore}
              onClick={() => setSize(size + 1)}
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </Flex>
        )}
        {eventTypes?.length === 0 && (
          <Flex
            mt={['20px', '60px', '60px', '80px']}
            justifyContent='center'
            alignItems='center'
            direction='column'
          >
            <Image src='/static/no-eventtypes.svg' alt='none' max='width' position='relative' left='30px' />
            <Text color='#101010' fontSize='1.5rem' textAlign='center' lineHeight='1.6875rem' marginBottom='15px' marginTop='43px' fontWeight={600}>
              No event types
            </Text>
            <Text color='#6A6E7D' maxWidth='429px' textAlign='center' marginBottom='40px'>
              Event types allow you to share links that show the time on your calendar and allow people to book appointments with you.
            </Text>
            <Button
              background='#4065FF'
              fontWeight='700'
              fontSize={['0.875rem', '1rem', '1rem', '1rem']}
              lineHeight='1.5rem' color='#fff' borderRadius='6px'
              height='40px'
              onClick={(e) => onOpenCreate()}
              _hover={{
                background: '#0031ff'
              }}
            >
              <Box
                as={FaPlus} size='17px' color='#fff'
                marginRight='7px'
              />New Event Type
            </Button>
          </Flex>
        )}
      </Container>

      <style jsx global>
        {`
          .custom-tabs > div {
            border: none;
          }
        
          .custom-tabs button {
            padding: 0;
            padding-bottom: 15px;
            padding-top: 16px;
            margin-right: 32px;
            color: #fff !important;
            opacity: 0.5;
            margin-bottom: 0 !important;
          }

          .custom-tabs button[aria-selected=true] {
            opacity: 1 !important;
            border-bottom: 4px solid;
          }

          .switcher > input[type=checkbox], .switcher > input[type=checkbox] + div {
            outline: none !important
          }

          .switcher > input[type=checkbox]:checked + div {
            background: #4065FF !important;
          }
        `}
      </style>
    </div>
  )
}

export default EventTypePage
